import type { PropertyDisplayCode } from 'src/api/generated';
import { formatPropertyCode } from 'src/modules/shared/utils/catalog/property-code.util';
import { useEffect, useMemo, useState } from 'react';
import {
  SearcherDocumentSearchFilterOperator,
  SettingName,
} from 'src/api/generated';
import type { SearcherDocumentSearchFilter, UserKind } from 'src/api/generated';
import { useFilters } from 'src/modules/shared/hooks/filters/filters';
import type { UseFiltersResult } from 'src/modules/shared/hooks/filters/filters';
import { useUsersSelect } from 'src/modules/shared/hooks/users/select';
import { UNASSIGNED_USER_ID } from '../../../constants/filters.const';
import type {
  Filter,
  StatusOption,
} from 'src/modules/shared/types/filters.types';
import { useBooleanSetting } from 'hooks/settings/settings';
import { useConversationFields } from './fields';
import { useStatusSelect } from 'src/modules/shared/hooks/api/crm/funnels/status-select';
import {
  ARCHIVE_STATUS_OPTIONS,
  ArchiveStatus,
} from 'src/modules/communications/types/filters.type';
import { useAllPersonAndSystemUsers } from 'src/modules/communications/hooks/users/system-users';
import { useProperties } from 'src/modules/shared/hooks/api/catalog/properties';
import { createQuery } from 'src/modules/shared/utils/api/query-builder.util';

export interface UseConversationFiltersProps {
  selectedFunnelId?: number;
}

export interface UseConversationFiltersResult extends UseFiltersResult {
  selectedUserIds: number[];
  setSelectedUserIds: (userIds: number[]) => void;
  userOptions: { value: number; label: string; kind: UserKind }[];
  statusOptions: StatusOption[];
  propertyOptions: { id: number; label: string; value: string }[];
  propertyLoading: boolean;
  onPropertyQueryChange: (query: string) => void;
  loading: boolean;
  resetFilters: () => void;
}

const AUTO_USER_FILTER_ID_PREFIX = 'filter-auto-userId';

const isAutoUserFilter = (filter: Filter) => {
  return (
    filter.fieldOptionId === 'userId' &&
    filter.id.startsWith(AUTO_USER_FILTER_ID_PREFIX)
  );
};

const buildAutoUserFilters = (userIds: number[]): Filter[] => {
  return userIds.map((userId) => ({
    id: `${AUTO_USER_FILTER_ID_PREFIX}-${userId}`,
    fieldOptionId: 'userId',
    operator: SearcherDocumentSearchFilterOperator.EQ,
    value: userId,
  }));
};

/**
 * Processes special filter fields that require transformation before being sent to the API.
 *
 * This hook specifically handles the conversion of UI-friendly 'archivedStatus' filters
 * into API-compatible 'archivedAt' filters with the appropriate operators.
 *
 * @param originalApiFilters - The original set of API filters to process
 * @param filters - The component-level filters containing potential special fields
 * @returns Processed API filters with special fields transformed appropriately
 */
const useProcessSpecialFields = (
  originalApiFilters: SearcherDocumentSearchFilter[],
  filters: Filter[]
) => {
  return useMemo(() => {
    const result = [...originalApiFilters];

    const withoutArchivedStatus = result.filter(
      (filter) => filter.field !== 'archivedStatus'
    );

    const archivedStatusFilter = filters.find(
      (filter) => filter.fieldOptionId === 'archivedStatus'
    );

    if (
      archivedStatusFilter?.value !== undefined &&
      archivedStatusFilter.value !== ''
    ) {
      const statusValue = archivedStatusFilter.value as string;

      const withoutArchivedAt = withoutArchivedStatus.filter(
        (filter) => filter.field !== 'archivedAt'
      );

      const statusIndex = Number(statusValue);
      let statusIndexString = String(statusValue);
      if (
        !isNaN(statusIndex) &&
        statusIndex >= 0 &&
        statusIndex < ARCHIVE_STATUS_OPTIONS.length
      ) {
        statusIndexString = String(ARCHIVE_STATUS_OPTIONS[statusIndex].value);
      } else {
        statusIndexString = String(statusValue);
      }

      if (statusIndexString === String(ArchiveStatus.ARCHIVED)) {
        withoutArchivedAt.push({
          field: 'archivedAt',
          operator: SearcherDocumentSearchFilterOperator.EXISTS,
        });
      } else if (statusIndexString === String(ArchiveStatus.ACTIVE)) {
        withoutArchivedAt.push({
          field: 'archivedAt',
          operator: SearcherDocumentSearchFilterOperator.MISSING,
        });
      }

      return withoutArchivedAt;
    }

    if (withoutArchivedStatus.some((filter) => filter.field === 'archivedAt')) {
      return withoutArchivedStatus;
    }

    return [
      ...withoutArchivedStatus,
      {
        field: 'archivedAt',
        operator: SearcherDocumentSearchFilterOperator.MISSING,
      },
    ];
  }, [originalApiFilters, filters]);
};

export function useConversationFilters({
  selectedFunnelId,
}: UseConversationFiltersProps): UseConversationFiltersResult {
  const { users, loading: isLoadingUsers } = useAllPersonAndSystemUsers();
  const { statusOptions, loading: isLoadingStatuses } = useStatusSelect({
    selectedFunnelId,
  });

  const {
    setting: showAllConversations,
    loading: loadingShowAllConversations,
  } = useBooleanSetting(SettingName.FEATURE_FLAG_AGENT_SHOW_ALL_CONVERSATIONS);

  const {
    selectedUserIds: userSelectIds,
    setSelectedUserIds: setUserSelectIds,
    userOptions,
    loading: isLoadingUserSelect,
    resetToDefaults: resetUsersSelectionToDefaults,
  } = useUsersSelect({ users });

  const loading =
    isLoadingUserSelect ||
    isLoadingUsers ||
    loadingShowAllConversations ||
    isLoadingStatuses;

  const { filterFieldsOptions } = useConversationFields({
    statusOptions,
  });

  const initialFilters = useMemo(() => {
    if (loading) return [];

    return [
      ...(userSelectIds.length > 0
        ? buildAutoUserFilters([userSelectIds[0]])
        : []),
      {
        id: 'filter-default-archivedStatus',
        fieldOptionId: 'archivedStatus',
        operator: SearcherDocumentSearchFilterOperator.EQ,
        value: ArchiveStatus.ACTIVE,
      },
    ];
  }, [userSelectIds, loading]);

  const overrideFilters = useMemo(() => {
    if (userSelectIds.length === 0 || loading || showAllConversations) {
      return [];
    }

    return buildAutoUserFilters(userSelectIds);
  }, [userSelectIds, showAllConversations, loading]);

  const {
    filters,
    apiFilters: originalApiFilters,
    setFilters,
    activeFilterCount,
    clearFilters,
  } = useFilters({
    fieldsOptions: filterFieldsOptions,
    overrideFilters,
    initialFilters,
  });

  useEffect(() => {
    if (loading || userSelectIds.length > 0) {
      return;
    }

    const userFilters = filters.filter(
      (filter) => filter.fieldOptionId === 'userId'
    );
    if (!userFilters.length || !userFilters.every(isAutoUserFilter)) {
      return;
    }

    setFilters(filters.filter((filter) => !isAutoUserFilter(filter)));
  }, [filters, loading, setFilters, userSelectIds.length]);

  const apiFilters = useProcessSpecialFields(originalApiFilters, filters);

  const [propertyQuery, setPropertyQuery] = useState('');

  const propertySearchParams = useMemo(() => {
    const builder = createQuery().take(20).orderByDesc('createdAt');

    const trimmed = propertyQuery.trim();
    if (trimmed.length > 0) {
      builder.search(trimmed);
    }

    return builder.build();
  }, [propertyQuery]);

  const selectedPropertyIds = useMemo(() => {
    const propertyFilters = filters.filter(
      (filter) => filter.fieldOptionId === 'propertyId'
    );

    const ids = propertyFilters.flatMap((filter) => {
      const raw = filter.value;

      if (typeof raw === 'number') return [raw];
      if (typeof raw === 'string') {
        const parsed = Number(raw);
        return Number.isFinite(parsed) ? [parsed] : [];
      }
      if (Array.isArray(raw)) {
        return raw.map((v) => Number(v)).filter((v) => Number.isFinite(v));
      }

      return [];
    });

    return Array.from(new Set(ids));
  }, [filters]);

  const selectedPropertiesQuery = useMemo(() => {
    if (selectedPropertyIds.length === 0) return null;

    return createQuery().equals('id', selectedPropertyIds).take(100).build();
  }, [selectedPropertyIds]);

  const { properties: searchedProperties, loading: propertyLoading } =
    useProperties({
      ...propertySearchParams,
      enabled: true,
    });

  const { properties: selectedProperties } = useProperties({
    ...(selectedPropertiesQuery ?? {}),
    enabled: selectedPropertyIds.length > 0,
  });

  const propertyOptions = useMemo(() => {
    const byId = new Map<
      number,
      { id: number; label: string; value: string }
    >();

    const toOption = (property: {
      id: number;
      name?: string | null;
      displayCode?: PropertyDisplayCode;
    }) => {
      const code = formatPropertyCode({
        displayCode: property.displayCode,
        fallback: `#${property.id}`,
      });
      const label = property.name?.trim() ? `${property.name} (${code})` : code;
      return { id: property.id, label, value: String(property.id) };
    };

    selectedProperties.forEach((p) => byId.set(p.id, toOption(p)));
    searchedProperties.forEach((p) => byId.set(p.id, toOption(p)));

    return Array.from(byId.values());
  }, [searchedProperties, selectedProperties]);

  const selectedUserIds = useMemo(() => {
    const userFilters = filters.filter(
      (filter) => filter.fieldOptionId === 'userId'
    );
    if (!userFilters.length) return [];

    return userFilters.flatMap((filter) => {
      if (filter.operator === SearcherDocumentSearchFilterOperator.MISSING) {
        return [UNASSIGNED_USER_ID];
      }

      if (filter.value) {
        return Array.isArray(filter.value)
          ? filter.value.map(Number)
          : [Number(filter.value)];
      }

      return [];
    });
  }, [filters]);

  const setSelectedUserIds = (userIds: number[]) => {
    if (showAllConversations) {
      setUserSelectIds(userIds);
    }

    const otherFilters = filters.filter(
      (filter) => filter.fieldOptionId !== 'userId'
    );

    if (userIds.length === 0) {
      setFilters(otherFilters);
      return;
    }

    if (userIds.includes(UNASSIGNED_USER_ID)) {
      const userFilter: Filter = {
        id: `filter-${Date.now()}-unassigned`,
        fieldOptionId: 'userId',
        operator: SearcherDocumentSearchFilterOperator.MISSING,
        value: undefined,
      };
      setFilters([...otherFilters, userFilter]);
      return;
    }

    const operator =
      userIds.length > 1
        ? SearcherDocumentSearchFilterOperator.IN
        : SearcherDocumentSearchFilterOperator.EQ;

    const value = userIds.length > 1 ? userIds : userIds[0];

    const userFilter: Filter = {
      id: `filter-${Date.now()}-${userIds[0]}`,
      fieldOptionId: 'userId',
      operator,
      value,
    };

    setFilters([...otherFilters, userFilter]);
  };

  const resetFiltersToDefaults = () => {
    if (showAllConversations) {
      setUserSelectIds([]);
    } else {
      resetUsersSelectionToDefaults();
    }
    clearFilters();
  };

  return {
    filters,
    apiFilters,
    setFilters,
    fieldsOptions: filterFieldsOptions,
    activeFilterCount,
    clearFilters,
    selectedUserIds,
    setSelectedUserIds,
    userOptions,
    loading,
    statusOptions,
    propertyOptions,
    propertyLoading,
    onPropertyQueryChange: setPropertyQuery,
    resetFilters: resetFiltersToDefaults,
  };
}
